import React, { useEffect, useState } from 'react'
import { DEFAULT_HISTORY_CARDS_COUNT, Gamble, type GambleResult } from '@amusnet/react-ui'
import { GambleGameMessageType, type GambleSnapshot, ViewportType } from 'game-ui-types'
import { useComplianceBarHeight } from '@hooks'
import { useConfigurationStore } from '@stores'

const readNumber = (value: unknown, fallback: number) => {
    return typeof value === 'number' ? value : fallback
}

const readHistory = (value: unknown) => {
    if (!Array.isArray(value)) {
        return []
    }

    return value.filter((card): card is number => typeof card === 'number')
}

const GambleGameWrapper: React.FC = () => {
    const shouldUseReactGambleGame = useConfigurationStore((state) => state.shouldUseReactGambleGame)
    const viewport = useConfigurationStore((state) => state.viewport)
    const currencyIso = useConfigurationStore((state) => state.currency)
    const hideCents = useConfigurationStore((state) => state.currencyHideCents)
    const [isOpen, setIsOpen] = useState(false)
    const [attemptsLeft, setAttemptsLeft] = useState(0)
    const [amount, setAmount] = useState(0)
    const [toWin, setToWin] = useState(0)
    const [history, setHistory] = useState<number[]>([])
    const [historyCardsCount, setHistoryCardsCount] = useState(DEFAULT_HISTORY_CARDS_COUNT)
    const [revealCard, setRevealCard] = useState<number | null>(null)
    const [result, setResult] = useState<GambleResult>(null)
    const complianceHeight = useComplianceBarHeight(isOpen)
    const orientation = viewport === ViewportType.MOBILE_PORTRAIT ? 'portrait' : 'landscape'

    useEffect(() => {
        const applySnapshot = (data: GambleSnapshot) => {
            setAttemptsLeft(readNumber(data.attemptsLeft, 0))
            setAmount(readNumber(data.amount, 0))
            setToWin(readNumber(data.toWin, 0))
            setHistory(readHistory(data.history))

            if (typeof data.historyCardsCount === 'number' && data.historyCardsCount > 0) {
                setHistoryCardsCount(data.historyCardsCount)
            }
        }

        const handleMessage = (event: MessageEvent) => {
            if (!useConfigurationStore.getState().shouldUseReactGambleGame) {
                return
            }

            const rawData = event.data

            if (!rawData || typeof rawData !== 'object') {
                return
            }

            switch (rawData.type) {
                case GambleGameMessageType.SHOW: {
                    applySnapshot(rawData)
                    setRevealCard(null)
                    setResult(null)
                    setIsOpen(true)
                    break
                }

                case GambleGameMessageType.UPDATE: {
                    applySnapshot(rawData)
                    break
                }

                case GambleGameMessageType.REVEAL: {
                    const roundResult = rawData.result === 'win' ? 'win' : 'lose'

                    applySnapshot(rawData)
                    setResult(roundResult)
                    setRevealCard(typeof rawData.card === 'number' ? rawData.card : null)
                    setIsOpen(true)
                    break
                }

                case GambleGameMessageType.HIDE: {
                    setRevealCard(null)
                    setResult(null)
                    setIsOpen(false)
                    break
                }

                default:
                    break
            }
        }

        window.uiBus.addEventListener('message', handleMessage, false)

        return () => {
            window.uiBus.removeEventListener('message', handleMessage, false)
        }
    }, [])

    const postCompleted = (type: string) => {
        window.uiBus.postMessage({ type })
    }

    const handleEnterComplete = () => {
        postCompleted(GambleGameMessageType.ENTER_COMPLETED)
    }

    const handleExitComplete = () => {
        setRevealCard(null)
        setResult(null)
        postCompleted(GambleGameMessageType.EXIT_COMPLETED)
    }

    const handleRevealComplete = () => {
        if (result === 'win') {
            return
        }

        setRevealCard(null)
        setResult(null)
    }

    const handleWinAnimationComplete = () => {
        setRevealCard(null)
        setResult(null)
        postCompleted(GambleGameMessageType.WIN_ANIMATION_COMPLETED)
    }

    if (!shouldUseReactGambleGame) {
        return null
    }

    return (
        <Gamble
            amount={amount}
            attemptsLeft={attemptsLeft}
            currency={{ iso: currencyIso, hideCents, showCurrencyCode: true }}
            history={history}
            historyCardsCount={historyCardsCount}
            open={isOpen}
            orientation={orientation}
            result={result}
            revealCard={revealCard}
            style={{ bottom: complianceHeight, zIndex: 1 }}
            toWin={toWin}
            onEnterComplete={handleEnterComplete}
            onExitComplete={handleExitComplete}
            onRevealComplete={handleRevealComplete}
            onWinAnimationComplete={handleWinAnimationComplete}
        />
    )
}

export default GambleGameWrapper
